import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell, type AppSection } from "./layouts/AppShell";
import type { PortfolioCreationRequest } from "./portfolio/models/PortfolioPosition";
import { AlertsCenter } from "./alerts/AlertsCenter";
import { alertRepository } from "./alerts/repository";
import { evaluateAlerts } from "./alerts/engine";
import { HistoricalObservationRepository } from "./intelligence/history/HistoricalObservationRepository";
import type { SmartAlert } from "./alerts/models";
import { PortfolioRepository } from "./portfolio/services/PortfolioRepository";
import { watchlistRepository } from "./watchlist/services/WatchlistRepository";
import type { OpportunityFeedItem } from "./feed/models";
import { calendarRepository } from "./calendar/repository";
import { buildSearchIndex } from "./search/buildSearchIndex";
import { aggregateOpportunityFeed } from "./feed/engine";
import { feedRepository } from "./feed/repository";
import type { SearchItem } from "./search/models";
import {
  legacyDeepLinkToAction,
  resolveAction,
  type AppAction,
  type PositionOpeningState,
} from "./actions/actionRouter";
import { readOnboardingPreference, type GettingStartedKey } from "./onboarding/onboarding";
import { marketPortfolioRequest } from "./features/market/marketPortfolioRequest";
import { createCompleteJsonExportText } from "./data-export/exportService";
import { downloadBlob } from "./data-export/browserDownload";
import { recordExternalBackupSuccess } from "./storage/storageHealth";
import type { TicketmasterEventDetail } from "./types/ticketmaster";

const Dashboard = lazy(() => import("./features/dashboard/Dashboard").then(module => ({ default: module.Dashboard })));
const TicketmasterMarket = lazy(() => import("./features/market/TicketmasterMarket").then(module => ({ default: module.TicketmasterMarket })));
const Portfolio = lazy(() => import("./features/portfolio/Portfolio").then(module => ({ default: module.Portfolio })));
const ProfitLabWorkspace = lazy(() => import("./profit-lab/ProfitLabWorkspace").then(module => ({ default: module.ProfitLabWorkspace })));
const WatchlistPage = lazy(() => import("./watchlist/components/WatchlistPage").then(module => ({ default: module.WatchlistPage })));
const ProviderConnectionCenter = lazy(() => import("./features/providers/ProviderConnectionCenter").then(module => ({ default: module.ProviderConnectionCenter })));
const OpportunityFeed = lazy(() => import("./feed/OpportunityFeed").then(module => ({ default: module.OpportunityFeed })));
const MarketCalendar = lazy(() => import("./calendar/MarketCalendar").then(module => ({ default: module.MarketCalendar })));
const SpotlightSearch = lazy(() => import("./search/SpotlightSearch").then(module => ({ default: module.SpotlightSearch })));
const OnboardingDialog = lazy(() => import("./onboarding/OnboardingDialog").then(module => ({ default: module.OnboardingDialog })));
const OpportunityRanking = lazy(() => import("./intelligence/OpportunityRanking/components/OpportunityRanking").then(module => ({ default: module.OpportunityRanking })));
const IntelligenceWorkspace = lazy(() => import("./intelligence/workspace/IntelligenceWorkspace").then(module => ({ default: module.IntelligenceWorkspace })));
const AccountSettings = lazy(() => import("./features/settings/AccountSettings").then(module => ({ default: module.AccountSettings })));
const MyOpportunities = lazy(() => import("./my-opportunities/MyOpportunities").then(module => ({ default: module.MyOpportunities })));

const RouteFallback = () => <div className="page-content" role="status" aria-live="polite">Načítání sekce…</div>;

export default function App() {
  const purchaseOpenerRef = useRef<HTMLElement | null>(null);
  const [onboardingOpen,setOnboardingOpen]=useState(()=>readOnboardingPreference().status==='new');
  const [section, setSection] = useState<AppSection>("dashboard");
  const [searchOpen, setSearchOpen] = useState(false);
  const [portfolioCreateRequest, setPortfolioCreateRequest] =
    useState<PortfolioCreationRequest | null>(null);
  const [portfolioPositionId, setPortfolioPositionId] = useState<string | null>(
    null,
  );
  const [positionOpening, setPositionOpening] =
    useState<PositionOpeningState | null>(null);
  const [watchlistItemId, setWatchlistItemId] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<SmartAlert[]>(() =>
    alertRepository.list(),
  );
  const refreshAlerts = useCallback(() => {
    const next = evaluateAlerts({
      previousAlerts: alertRepository.list(),
      observations: new HistoricalObservationRepository().query({limit:500,order:'asc'}),
    });
    alertRepository.replaceAll(next);
    setAlerts(next);
  }, []);
  useEffect(() => {
    window.addEventListener("historical-observations-change", refreshAlerts);
    const read = () => setAlerts(alertRepository.list());
    window.addEventListener("alerts-change", read);
    return () => {
      window.removeEventListener("historical-observations-change", refreshAlerts);
      window.removeEventListener("alerts-change", read);
    };
  }, [refreshAlerts]);
  const executeAction = useCallback((action: AppAction | null) => {
    if (!action) return;
    const destination = resolveAction(action);
    if (destination.section === "portfolio") {
      const exists = new PortfolioRepository()
        .list()
        .some((position) => position.id === destination.entityId);
      if (!exists) {
        setPortfolioPositionId(null);
        setPositionOpening(null);
        setSection("portfolio");
        return;
      }
      setPortfolioPositionId(destination.entityId ?? null);
      setPositionOpening(destination.position ?? null);
    }
    if (destination.section === "watchlist")
      setWatchlistItemId(destination.entityId ?? null);
    setSection(destination.section);
  }, []);
  const openAlert = (alert: SmartAlert) =>
    executeAction(legacyDeepLinkToAction(alert.actionTarget));
  const feedItems = useMemo(
    () =>
      aggregateOpportunityFeed({
        alerts,
        lifecycle: feedRepository.lifecycle(),
      }),
    [alerts],
  );
  const searchItems = useMemo(
    () =>
      buildSearchIndex({
        positions: new PortfolioRepository().list(),
        watchlist: watchlistRepository.list(),
        alerts,
        feed: feedItems,
        calendar: calendarRepository.list(),
      }),
    [alerts, feedItems, section],
  );
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  const openSearchItem = (item: SearchItem) =>
    executeAction(legacyDeepLinkToAction(item.deepLink));
  const openFeedItem = (item: OpportunityFeedItem) =>
    executeAction(legacyDeepLinkToAction(item.deepLink));
  function createPurchase() {
    purchaseOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSection("portfolio");
    setPortfolioPositionId(null);
    setPortfolioCreateRequest({
      requestId: crypto.randomUUID(),
      source: "manual",
      watchlistItemId: "",
      initialValues: {},
    });
  }
  const createBackup=()=>{downloadBlob(createCompleteJsonExportText(new PortfolioRepository().list(),watchlistRepository.list()),`valemato-backup-${new Date().toISOString().slice(0,10)}.json`,'application/json');recordExternalBackupSuccess();};
  const gettingStartedAction=(key:GettingStartedKey)=>{if(key==='purchase')return createPurchase();if(key==='backup')return createBackup();executeAction(key==='market'?{type:'open-event',payload:{eventId:''}}:key==='watchlist'?{type:'open-watchlist',payload:{watchlistId:''}}:{type:'open-provider',payload:{providerId:''}})};
  return (
    <><Suspense fallback={null}>{onboardingOpen ? <OnboardingDialog open onClose={()=>setOnboardingOpen(false)} /> : null}</Suspense>
    <AppShell
      active={section}
      onNavigate={setSection}
      onNewPurchase={createPurchase}
      onSearch={() => setSearchOpen(true)}
    >
      <Suspense fallback={null}>{searchOpen ? <SpotlightSearch
        items={searchItems}
        open
        onClose={() => setSearchOpen(false)}
        onOpen={openSearchItem}
      /> : null}</Suspense>
      <Suspense fallback={<RouteFallback />}>
      {section === "my-opportunities" ? <MyOpportunities onNavigate={setSection} onOpenPortfolio={(id)=>executeAction({type:'open-position',payload:{positionId:id}})} /> : section === "ranking" ? <OpportunityRanking onNavigate={setSection} watchlistItems={watchlistRepository.list()} portfolioPositions={new PortfolioRepository().list()} /> : section === "dashboard" ? (
        <Dashboard
          onOpenMarket={() => setSection("market")}
          onCreatePurchase={createPurchase}
          onOpenPortfolio={(id) =>
            id
              ? executeAction({
                  type: "open-position",
                  payload: { positionId: id },
                })
              : setSection("portfolio")
          }
          onOpenWatchlist={() => setSection("watchlist")}
          onOpenAlerts={() => setSection("alerts")}
          onOpenFeed={() => setSection("feed")}
          onOpenCalendar={() => setSection("calendar")}
          onGettingStarted={gettingStartedAction}
          onOpenProfit={() => setSection("profit")}
        />
      ) : section === "feed" ? (
        <OpportunityFeed alerts={alerts} onOpen={openFeedItem} />
      ) : section === "alerts" ? (
        <AlertsCenter
          alerts={alerts}
          onChanged={() => setAlerts(alertRepository.list())}
          onOpen={openAlert}
        />
      ) : section === "market" ? (
        <TicketmasterMarket onAddToPortfolio={(event:TicketmasterEventDetail) => {
          setPortfolioPositionId(null);
          setPortfolioCreateRequest(marketPortfolioRequest(event));
          setSection('portfolio');
        }} />
      ) : section === "portfolio" ? (
        <Portfolio
          createRequest={portfolioCreateRequest}
          openPositionId={portfolioPositionId}
          openingState={positionOpening}
          onOpeningHandled={() => setPositionOpening(null)}
          onRequestHandled={() => setPortfolioCreateRequest(null)}
          getCreateDialogReturnFocus={() => purchaseOpenerRef.current}
          onOpenWatchlist={(id) => {
            setWatchlistItemId(id);
            setSection("watchlist");
          }}
        />
      ) : section === "watchlist" ? (
        <WatchlistPage
          openItemId={watchlistItemId}
          onCreatePortfolio={(request) => {
            setPortfolioPositionId(null);
            setPortfolioCreateRequest(request);
            setSection("portfolio");
          }}
          onOpenPortfolio={(id) =>
            executeAction({
              type: "open-position",
              payload: { positionId: id },
            })
          }
        />
      ) : section === "providers" ? (
        <ProviderConnectionCenter />
      ) : section === "calendar" ? (
        <MarketCalendar
          positions={new PortfolioRepository().list()}
          watchlist={watchlistRepository.list()}
          alerts={alerts}
          onOpen={(link) =>
            executeAction(legacyDeepLinkToAction(link, link.saleId))
          }
        />
      ) : section === "profit" ? (
        <ProfitLabWorkspace />
      ) : section === "accounts" ? (
        <AccountSettings onReopenOnboarding={()=>setOnboardingOpen(true)} />
      ) : section === "intelligence" ? (
        <IntelligenceWorkspace onNavigate={setSection} />
      ) : (
        null
      )}
      </Suspense>
    </AppShell>
    </>
  );
}
